import React, { useState, useContext } from "react";
import { ThemeContext } from "../../context";
import "./navBar.css";

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const theme = useContext(ThemeContext);
    const { darkMode } = theme.state;

    const toggleDarkMode = () => {
        theme.dispatch({ type: "TOGGLE_DARK" });
    };

    const navLinks = [
        { label: 'Home', href: '#intro', icon: '👋' },
        { label: 'About', href: '#about', icon: '💼' },
        { label: 'Projects', href: '#projects', icon: '🎮' },
        { label: 'Experience', href: '#experience', icon: '📜' },
        { label: 'Skills', href: '#skills', icon: '🛠️' },
        { label: 'Blog', href: '#medium', icon: '✍️' },
        { label: 'Contact', href: '#contact', icon: '✉️' }
    ];

    return (
        <nav className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${
            darkMode ? 'bg-gray-950/80 border-gray-800' : 'bg-white/80 border-gray-200'
        } backdrop-blur-md border-b`}>
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between">
                
                {/* Left: Logo */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <div className={`text-lg md:text-xl font-bold tracking-wider ${
                        darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                        PS
                    </div>
                    <div className={`text-xs md:text-sm font-medium hidden sm:block ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Game Developer
                    </div>
                </div>

                {/* Center: Navigation Links (Desktop) */}
                <ul className="hidden lg:flex items-center gap-1">
                    {navLinks.map((link, idx) => (
                        <li key={idx}>
                            <a 
                                href={link.href}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors duration-300 ${
                                    darkMode 
                                        ? 'text-gray-300 hover:text-blue-400 hover:bg-gray-800' 
                                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100'
                                }`}
                                title={link.label}
                            >
                                <span className="text-lg">{link.icon}</span>
                                <span className="text-sm font-medium">{link.label}</span>
                            </a>
                        </li>
                    ))}
                </ul>

                {/* Right: Navigation Icons (Mobile/Tablet) + Dark Mode Toggle */}
                <div className="flex items-center gap-1 lg:hidden">
                    {navLinks.map((link, idx) => (
                        <a 
                            key={idx}
                            href={link.href}
                            className={`p-2 rounded-lg transition-colors duration-300 text-lg ${
                                darkMode 
                                    ? 'text-gray-300 hover:text-blue-400 hover:bg-gray-800' 
                                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100'
                            }`}
                            title={link.label}
                        >
                            {link.icon}
                        </a>
                    ))}
                </div>

                {/* Dark Mode Toggle & Mobile Menu */}
                <div className="flex items-center gap-2 lg:absolute lg:right-4">
                    <button
                        onClick={toggleDarkMode}
                        className={`p-2 rounded-lg transition-colors duration-300 ${
                            darkMode 
                                ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        aria-label="Toggle dark mode"
                        title={darkMode ? "Light Mode" : "Dark Mode"}
                    >
                        {darkMode ? '☀️' : '🌙'}
                    </button>

                    {/* Mobile Menu Toggle */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className={`lg:hidden p-2 rounded-lg transition-colors duration-300 ${
                            darkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                        }`}
                        aria-label="Toggle menu"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {isOpen && (
                <div className={`lg:hidden border-t ${
                    darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
                } transition-colors duration-300`}>
                    <div className="px-4 py-4 space-y-2">
                        {navLinks.map((link, idx) => (
                            <a 
                                key={idx}
                                href={link.href}
                                onClick={() => setIsOpen(false)}
                                className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors duration-300 ${
                                    darkMode 
                                        ? 'text-gray-300 hover:bg-gray-800' 
                                        : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                <span className="text-lg">{link.icon}</span>
                                <span>{link.label}</span>
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
